import styled from 'styled-components';
import { Button } from '../../../../../../../components';

export const ModalContent = styled.div`
  width: min(980px, 92vw);
  max-height: 90vh;
  overflow-y: auto;
  background: ${props => props.theme.colors.white};
  border-radius: 12px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.2);
`;

export const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 24px;
`;

export const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #2c3941;
  margin: 0;
`;

export const ModalSubtitle = styled.p`
  font-size: 14px;
  color: #6b7b86;
  margin: 0;
`;

export const ModalSubHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

export const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const LegendSwatch = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: rgba(255, 159, 67, 0.14);
  box-shadow: inset 0 0 0 1px rgba(255, 159, 67, 0.5);
`;

export const LegendText = styled.span`
  font-size: 12px;
  color: #6b7b86;
`;

export const Section = styled.section`
  border: 1px solid #e2e8ef;
  border-radius: 10px;
  padding: 16px 18px;
  background: #f8fafb;
  & + & {
    margin-top: 16px;
  }
`;

export const TopSplit = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
  margin-bottom: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const TopSplitItem = styled.div`
  display: block;
  width: 100%;
`;

export const MapContainer = styled.div`
  width: 100%;
  height: 220px;
  border-radius: 8px;
  overflow: hidden;
  background: #eef2f5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const MapPlaceholder = styled.span`
  font-size: 12px;
  color: #8b97a2;
`;

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2c3941;
  margin: 0 0 12px;
`;

export const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px 16px;
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

export const Item = styled.div<{ $dirty?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${props =>
    props.$dirty ? 'rgba(255, 159, 67, 0.14)' : 'transparent'};
  box-shadow: ${props =>
    props.$dirty ? 'inset 0 0 0 1px rgba(255, 159, 67, 0.5)' : 'none'};
`;

export const ItemWide = styled.div<{ $dirty?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  grid-column: 1 / -1;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${props =>
    props.$dirty ? 'rgba(255, 159, 67, 0.14)' : 'transparent'};
  box-shadow: ${props =>
    props.$dirty ? 'inset 0 0 0 1px rgba(255, 159, 67, 0.5)' : 'none'};
`;

export const ItemLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8b97a2;
`;

export const ItemValue = styled.span`
  font-size: 14px;
  color: #2c3941;
  line-height: 1.35;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const ValuePlaceholder = styled.span`
  font-size: 14px;
  color: #9aa4ad;
  font-style: italic;
`;

export const ValueList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const ValueChip = styled.span`
  background: ${props => props.theme.colors.white};
  border: 1px solid #d9e0e6;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: #2c3941;
`;

export const ListCard = styled.div`
  background: ${props => props.theme.colors.white};
  border: 1px solid #e1e7ee;
  border-radius: 8px;
  padding: 12px 14px;
  & + & {
    margin-top: 12px;
  }
`;

export const ListCardHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #2c3941;
  margin-bottom: 10px;
`;

export const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

export const ButtonOutline = styled(Button).attrs({
  variant: 'outline',
  size: 'md'
})`
  min-width: 160px;
`;

export const ButtonPrimary = styled(Button).attrs({
  variant: 'secondary',
  size: 'md'
})`
  min-width: 180px;
`;

export const DiffCompareGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const DiffValueBase = styled.pre`
  margin: 0;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.border};
  font-size: ${props => props.theme.typography.sizes.xs};
  line-height: ${props => props.theme.typography.lineHeights.sm};
  white-space: pre-wrap;
  word-break: break-word;
  color: ${props => props.theme.colors.textPrimary};
`;

export const DiffValueBefore = styled(DiffValueBase)`
  border-color: ${props => props.theme.colors.danger};
  background: ${props => props.theme.colors.background};
`;

export const DiffValueAfter = styled(DiffValueBase)`
  border-color: ${props => props.theme.colors.success};
  background: ${props => props.theme.colors.background};
`;

export const GroupDetails = styled.details`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  padding: 8px 10px;
  background: ${props => props.theme.colors.white};

  & + & {
    margin-top: 10px;
  }
`;

export const GroupSummary = styled.summary`
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.typography.weights.semibold};
`;

export const GroupMeta = styled.span`
  margin-left: 8px;
  color: ${props => props.theme.colors.textMuted};
  font-size: ${props => props.theme.typography.sizes.xs};
`;
